export const GB = 1024 * 1024 * 1024;
export const WARN_RATIO = 0.8;
export const DEFAULT_STORAGE_LIMIT_GB = 50;
export const DEFAULT_OPERATION_LIMIT = 10000;

export class QuotaError extends Error {
  constructor(kind, usage) {
    super(kind === "storage" ? "QUOTA_STORAGE" : "QUOTA_OPERATIONS");
    this.code = this.message;
    this.kind = kind;
    this.usage = usage;
  }
}

/**
 * Normalises `usage/{uid}`: web writes `operations` / `storageUsed` (GB),
 * older mobile builds `operationCount` / `storageUsedBytes`. Both only grow,
 * so the larger value is the most recent one.
 */
export function readUsage(data = {}) {
  const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  return {
    opsUsed: Math.max(num(data.operations), num(data.operationCount)),
    opsLimit: num(data.operationLimit) || DEFAULT_OPERATION_LIMIT,
    bytesUsed: Math.max(num(data.storageUsedBytes), num(data.storageUsed) * GB),
    bytesLimit: num(data.storageLimitBytes) || (num(data.storageLimit) || DEFAULT_STORAGE_LIMIT_GB) * GB
  };
}

/** Result of adding `bytes` / `ops` to the current usage: blocked above 100%, warned from 80%. */
export function evaluateQuota(usage, { bytes = 0, ops = 1 } = {}) {
  const bytesAfter = usage.bytesUsed + bytes;
  const opsAfter = usage.opsUsed + ops;
  const storageRatio = bytesAfter / usage.bytesLimit;
  const opsRatio = opsAfter / usage.opsLimit;
  const blocked = storageRatio > 1 ? "storage" : opsRatio > 1 ? "operations" : null;
  const warnings = [];
  if (storageRatio >= WARN_RATIO) warnings.push("storage");
  if (opsRatio >= WARN_RATIO) warnings.push("operations");
  return {
    blocked,
    warnings,
    storagePct: Math.min(100, Math.round(storageRatio * 100)),
    opsPct: Math.min(100, Math.round(opsRatio * 100)),
    bytesAfter,
    opsAfter
  };
}

/** Kinds whose 80% threshold is crossed by this change (notify once, on the crossing). */
export function crossedWarning(usage, { bytes = 0, ops = 1 } = {}) {
  const out = [];
  if (usage.bytesUsed < WARN_RATIO * usage.bytesLimit && usage.bytesUsed + bytes >= WARN_RATIO * usage.bytesLimit) out.push("storage");
  if (usage.opsUsed < WARN_RATIO * usage.opsLimit && usage.opsUsed + ops >= WARN_RATIO * usage.opsLimit) out.push("operations");
  return out;
}
